import {
  List,
  ListItemButton,
  Radio,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  type RestoreAccount,
  type Provider,
  type Snapshot,
  AccountState,
} from '@repo/api-gw-sdk';
import { type Dispatch, type SetStateAction } from 'react';

import { AccountIndicator } from '@/components/accounts/accountIndicator';
import { Loader } from '@/components/layout/loading';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useIsAccountFeatureSupported } from '@/utils/useIsAccountFeatureSupported';

import { StepContainer } from '../../wizard/StepContainer';

interface RestoreAccountProps {
  supportedCloudProviders: Provider[];
  restoreAccount?: RestoreAccount;
  restoreToTime?: Date;
  isPiTR?: boolean;
  snapshot?: Snapshot;
  supportedInstalledVersion?: string;
  sourceProvider?: Provider;
  sourceRegion?: string;
}

type SelectTargetAccountStepProps<T> = StepProps<T & RestoreAccountProps>;

export const SelectTargetAccountStep = <T,>(
  props: SelectTargetAccountStepProps<T>
) => {
  const backupVaultInfo =
    props.wizardState.sourceProvider &&
    props.wizardState.sourceRegion !== undefined
      ? {
          cloudProvider: props.wizardState.sourceProvider,
          region: props.wizardState.sourceRegion,
        }
      : undefined;

  return (
    <StepContainer
      stepperLabels={props.stepperLabels}
      stepperIndex={Math.max(props.stepperLabels.indexOf('Restore Account'), 0)}
      onBackClick={props.back}
      canGoNext={() => !!props.wizardState.restoreAccount}
      onNextClick={async () => props.setNextStep(props.currentStep.next?.[0])}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot?.pointInTime}
          restoreToTime={props.wizardState.restoreToTime}
          vaultId={props.wizardState.snapshot?.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot?.accessDeniedItemsCount
          }
          supportPiTR={
            props.wizardState.isPiTR || props.wizardState.snapshot?.supportPiTR
          }
          backupVaultInfo={backupVaultInfo}
        />
      }
    >
      <SelectTargetAccount
        {...props.wizardState}
        setRestoreAccountState={props.setWizardState}
      />
    </StepContainer>
  );
};

interface SelectTargetAccountProps<T> extends RestoreAccountProps {
  setRestoreAccountState: Dispatch<
    SetStateAction<
      T & {
        restoreAccount?: RestoreAccount;
      }
    >
  >;
  title?: string;
  accounts?: RestoreAccount[];
  emptyMessage?: string;
  isAccountDisabled?: (account: RestoreAccount) => boolean;
}

export const SelectTargetAccount = <T,>(props: SelectTargetAccountProps<T>) => {
  const dal = useDAL();
  const { isDemo, isDev } = useEnvironment();
  const isAccountFeatureSupported = useIsAccountFeatureSupported();
  const { data: body } = dal.cloudAccounts.restore.useList();

  // Use provided accounts or fetch internally
  const accounts = props.accounts ?? body?.accounts ?? [];

  if (!props.accounts && !body) {
    return <Loader />;
  }

  const onRestoreAccountClicked = (account: RestoreAccount) => {
    props.setRestoreAccountState((state) => ({
      ...state,
      restoreAccount: account,
    }));
  };

  const filteredAccounts = accounts.filter(
    (account) =>
      account.status === AccountState.ACCOUNT_STATE_CONNECTED &&
      (!props.supportedCloudProviders ||
        props.supportedCloudProviders.includes(
          account.restoreAccountAttributes.cloudProvider
        ))
  );

  const getIsDisabled = (account: RestoreAccount): boolean => {
    if (props.isAccountDisabled) {
      return props.isAccountDisabled(account);
    }
    // Default disabled logic
    return Boolean(
      !isDemo &&
      !isDev &&
      props.supportedInstalledVersion &&
      !isAccountFeatureSupported(props.supportedInstalledVersion, account)
    );
  };

  return (
    <>
      <Typography component={'span'}>
        {props.title || 'Select an account to restore to:'}
      </Typography>
      {filteredAccounts.length === 0 && props.emptyMessage ? (
        // empty state message
        <Typography
          variant='subtitle2'
          color='text.secondary'
          className='mt-[20px]'
        >
          {props.emptyMessage}
        </Typography>
      ) : (
        <List className='mt-[20px] mb-[10px]'>
          {filteredAccounts.map((account) => {
            const isChecked = props.restoreAccount?.id === account.id;
            const isDisabled = getIsDisabled(account);
            return (
              <Tooltip
                key={account.id}
                title={
                  isDisabled
                    ? "This account doesn't have the required permissions. Please update the account's role"
                    : undefined
                }
              >
                <span>
                  <ListItemButton
                    key={account.id}
                    sx={{
                      border: isChecked
                        ? '2px solid var(--mui-palette-primary-main)'
                        : '1px solid var(--mui-palette-divider)',
                      padding: isChecked ? '16px 24px' : '17px 25px',
                      marginBottom: '8px',
                    }}
                    onClick={() => onRestoreAccountClicked(account)}
                    className='flex'
                    disabled={isDisabled}
                  >
                    <Radio
                      className='p-0'
                      checked={isChecked}
                      value={account.id}
                    />
                    <AccountIndicator account={account} copyable={false} />
                  </ListItemButton>
                </span>
              </Tooltip>
            );
          })}
        </List>
      )}
    </>
  );
};
